import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import PersonAttributes from '../../aml/sections/PersonAttributes';
import { ApiSanctionAlert, ApiSanctionEntityType, ApiSanctionHitType, SanctionHitsPage } from '../../api';
import Matches from './matches/Matches';
import TransactionAttributes from '../../transaction/TransactionAttributes';
import { Documents } from '../../common/documents/Documents';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../organisation/feature/helper';
import { InternalApiCaseAttachmentType } from '../../api-internal';
import { CAN_READ_CASE } from '../../auth/permissions';
import Has from '../../auth/Has';
import AttachmentCases from '../../case/attachments/AttachmentCases';
import RFISending from '../../rfi-sending/RFISending';
import {
  InternalApiAlertType,
  InternalApiEntityType,
  InternalApiUniversalDocumentEntityType,
} from '../../api-internal';
import useRfiData from '../../rfi-sending/useRfiData';
import RFIAttributes from '../../rfi-sending/RFIAttributes';
import { AlertManagement, AlertPropertyChange } from '../../alerts/management/AlertManagement';
import RelatedAlertsStatusModal from '../RelatedAlertsStatusModal';
import { useMe } from '../../organisation/useMe';
import { sanctionHitApi } from '../../apis';
import { SalvButton, SalvModal } from '@salvapp/ui-components';

export const ScreeningInfo = ({
  sanctionAlertId,
  alert,
  onChange,
}: {
  sanctionAlertId: number;
  alert: ApiSanctionAlert;
  onChange?: () => void;
}) => {
  const isPerson = alert.entity.entityType === ApiSanctionEntityType.PERSON;
  const { person, transaction } = alert.entity;
  const highlightedFields = alert.fieldKey?.split(' ');
  const { features } = useMe();
  const emptyPage = {
    number: 0,
    size: 0,
    total: 0,
    content: [],
  };
  const [initialFilteredHits, setInitialFilteredHits] = useState<SanctionHitsPage>(emptyPage);
  const [initialActiveHits, setInitialActiveHits] = useState<SanctionHitsPage>(emptyPage);
  const [showModal, setShowModal] = useState(false);

  const [showRelatedAlertModel, setShowRelatedAlertModel] = useState<AlertPropertyChange>();
  const statusChangeCallback = useCallback(
    (change: AlertPropertyChange) => {
      onChange?.();
      if (change?.newStatus?.isGoodlistable) {
        setShowRelatedAlertModel(change);
      }
    },
    [setShowRelatedAlertModel]
  );

  const isRfiEnabled = isFeatureEnabled(ORGANISATION_FEATURE.BRIDGE_MESSAGING_OUTGOING, features);
  const rfiData = useRfiData(alert.id, InternalApiAlertType.SCREENING, isRfiEnabled);

  const findInitialHits = useCallback(async () => {
    const activeHits = await sanctionHitApi.findSanctionHits({
      page: 0,
      size: 20,
      sanctionAlertId,
      type: ApiSanctionHitType.ACTIVE,
    });
    const filteredHits = await sanctionHitApi.findSanctionHits({
      page: 0,
      size: 20,
      sanctionAlertId,
      type: ApiSanctionHitType.FILTERED,
    });
    setInitialActiveHits(activeHits);
    setInitialFilteredHits(filteredHits);
  }, [setInitialActiveHits, setInitialFilteredHits, sanctionAlertId]);

  const validateShownHitsCallback = useCallback(async () => {
    const currentHits = await sanctionHitApi.findSanctionHits({
      page: 0,
      size: 1,
      sanctionAlertId,
      type: ApiSanctionHitType.ACTIVE,
    });
    const hitsDiffer = currentHits.total != initialActiveHits.total;
    if (hitsDiffer) {
      setShowModal(hitsDiffer);
      findInitialHits();
    }
    return hitsDiffer;
  }, [setShowModal, initialActiveHits]);

  useEffect(() => {
    findInitialHits();
  }, [findInitialHits, alert.customListId]);

  const screenedValue =
    isPerson && person
      ? highlightedFields?.map((fieldName) => person.attributes?.[fieldName]).join(' ')
      : transaction
        ? alert.matchSources?.map((matchSource) => transaction.attributes?.[matchSource.fieldKey!]).join(' ')
        : undefined;

  return (
    <Fragment>
      <SalvModal show={showModal} onHide={() => setShowModal(false)}>
        <SalvModal.Body>
          <div className='text-m'>
            Since opening the alert, additional hits have been added to the alert. Please review the new hits before
            continuing with status change.
          </div>
        </SalvModal.Body>
        <SalvModal.Footer>
          <SalvButton variant='secondary' label='Close' onClick={() => setShowModal(false)} />
        </SalvModal.Footer>
      </SalvModal>
      <Row>
        <Col md={4} className='sticky-col'>
          {isPerson && person ? (
            <PersonAttributes person={person} highlightedFields={highlightedFields} />
          ) : transaction ? (
            <TransactionAttributes
              transaction={transaction}
              showLinkToTransactionPage={false}
              showLinkToCustomerPage={false}
              highlightedFields={alert.matchSources!}
            />
          ) : null}
          {isRfiEnabled && <RFIAttributes rfiData={rfiData} matchSources={alert.matchSources!} />}
        </Col>
        <Col md={8}>
          <AlertManagement
            entityType={InternalApiEntityType[alert.entity.entityType]}
            alertId={alert.id}
            alertType={InternalApiAlertType.SCREENING}
            alertSubType={alert.type}
            customListId={alert.customListId}
            preStatusChangeValidation={validateShownHitsCallback}
            postAlertPropertyChange={statusChangeCallback}
          />
          {showRelatedAlertModel && showRelatedAlertModel.newStatus && (
            <RelatedAlertsStatusModal
              sanctionAlertId={alert.id}
              note={showRelatedAlertModel?.newNote || ''}
              status={showRelatedAlertModel?.newStatus}
              triggerLegacyAlertUpdate={false}
              onComplete={() => setShowRelatedAlertModel(undefined)}
            />
          )}
          {isRfiEnabled && <RFISending alert={alert} alertType={InternalApiAlertType.SCREENING} rfiData={rfiData} />}
          <Documents
            className='universal-documents--legacy'
            entityType={InternalApiUniversalDocumentEntityType.SCREENING_ALERT}
            entityId={sanctionAlertId}
            onChange={onChange}
          />
          {isFeatureEnabled(ORGANISATION_FEATURE.CASE_MANAGEMENT, features) && (
            <Has allPermissions={[CAN_READ_CASE]}>
              <AttachmentCases
                attachmentType={InternalApiCaseAttachmentType.SCREENING_ALERT}
                externalId={String(sanctionAlertId)}
              />
            </Has>
          )}
          <Matches
            sanctionAlertId={sanctionAlertId}
            alert={alert}
            rfiHandle={rfiData?.rfi?.id}
            initialActiveHits={initialActiveHits}
            initialFilteredHits={initialFilteredHits}
            screenedValue={screenedValue}
          />
        </Col>
      </Row>
    </Fragment>
  );
};

export default ScreeningInfo;
